import { useState, useEffect } from 'react';
import { Modal, Box, Button, TextField } from '@mui/material';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import '../translations.js';
import { EXAMPLE_BEHAVIORS } from '../constants/behaviorExamples.js';
import './addWebsiteLinkModal.css';


const AddBehaviorModal = ({ open, onClose, onSubmit, botId, remainingSlots, initialGuideline = '' }) => {
  const [guidelines, setGuidelines] = useState(['']);
  const [examplesOpen, setExamplesOpen] = useState(false);
  const { t } = useTranslation();

  // Seed the first field whenever the modal opens — blank for a plain "Add",
  // or pre-filled with an example the user picked on the empty-state cards.
  useEffect(() => {
    if (open) {
      setGuidelines([initialGuideline || '']);
      setExamplesOpen(false);
    }
  }, [open, initialGuideline]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const MAX_CHARS = ["17484411083762250247QKLKy", "175922482585356401336yPixb"].includes(botId) ? 1500 : 250;

  const handleInputChange = (index, value) => {
    setGuidelines(prev => prev.map((g, i) => (i === index ? value : g)));
  };

  const handleAddField = () => {
    setGuidelines(prev => [...prev, '']);
  };

  const handleRemoveField = (index) => {
    setGuidelines(prev => prev.filter((_, i) => i !== index));
  };

  // Clicking an example drops its text into the first empty field, or adds a
  // new field for it when there's room, so picking one never overwrites text
  // the user has already typed.
  const applyExample = (text) => {
    setGuidelines(prev => {
      const emptyIdx = prev.findIndex(g => g.trim() === '');
      if (emptyIdx !== -1) {
        return prev.map((g, i) => (i === emptyIdx ? text : g));
      }
      const canAdd = remainingSlots === undefined || prev.length < remainingSlots;
      return canAdd ? [...prev, text] : prev.map((g, i) => (i === prev.length - 1 ? text : g));
    });
  };

  const handleSubmit = () => {
    const cleaned = guidelines.map(g => g.trim()).filter(Boolean);
    if (cleaned.length === 0) return;
    onSubmit({ new_guidelines: cleaned, bot_id: botId });
    setGuidelines(['']);
    onClose();
  };

  const handleClose = () => {
    setGuidelines(['']);
    onClose();
  };

  const filledCount = guidelines.filter(g => g.trim().length > 0).length;
  const canAddMore = remainingSlots === undefined || guidelines.length < remainingSlots;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          p: isMobile ? 2.5 : 3.5,
          width: isMobile ? '80%' : '90%',
          maxWidth: 600,
          maxHeight: '80vh',
          overflowY: 'auto',
          outline: 'none',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>{t('add_behavior')}</h3>

        {guidelines.map((guideline, idx) => {
          const charsUsed = guideline.length;
          const atLimit = charsUsed === MAX_CHARS;

          return (
            <Box
              key={idx}
              display="flex"
              alignItems="flex-start"
              className="fnl-row"
            >
              <TextField
                fullWidth
                label={`${t('behavior_description')}${guidelines.length > 1 ? ` ${idx + 1}` : ''}`}
                variant="outlined"
                value={guideline}
                onChange={e => handleInputChange(idx, e.target.value)}
                required
                multiline
                minRows={2}
                inputProps={{ maxLength: MAX_CHARS }}
                helperText={`${charsUsed}/${MAX_CHARS}`}
                FormHelperTextProps={{
                  sx: {
                    textAlign: 'right',
                    mr: 0,
                    color: atLimit ? 'error.main' : 'text.secondary',
                  },
                }}
                sx={{
                  '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                    borderColor: '#5e1bff',
                    borderWidth: '1px',
                  },
                  '& .MuiInputLabel-root': {
                    color: 'gray',
                    '&.Mui-focused': { color: '#5e1bff' },
                  },
                }}
              />
              {guidelines.length > 1 && (
                <button
                  onClick={() => handleRemoveField(idx)}
                  className="remove-url-button"
                  title={t('remove')}
                  style={{
                    marginLeft: 8,
                    alignSelf: 'center',
                    marginBottom: 22,
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#9e9e9e',
                  }}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </Box>
          );
        })}

        {canAddMore && (
          <button
            onClick={handleAddField}
            className="add-url-button"
            title={t('add_behavior')}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: 0,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: '#5616ea',
              margin: '-4px 0 16px 0',
            }}
          >
            <FontAwesomeIcon icon={faPlus} />
            {t('add_behavior')}
          </button>
        )}

        {/* Examples — collapsed link; expand to browse, click one to prefill. */}
        <div className="behavior-modal-examples">
          <button
            type="button"
            className="behavior-modal-examples-toggle"
            onClick={() => setExamplesOpen(o => !o)}
            aria-expanded={examplesOpen}
          >
            {t('behavior_examples_toggle')}
            <FontAwesomeIcon
              icon={faChevronDown}
              className={`behavior-modal-examples-chevron${examplesOpen ? ' open' : ''}`}
            />
          </button>

          <div className={`behavior-modal-examples-wrap${examplesOpen ? ' open' : ''}`}>
            <div className="behavior-modal-examples-inner">
              <div className="behavior-modal-examples-list">
                {EXAMPLE_BEHAVIORS.map((example) => (
                  <button
                    key={example.title}
                    type="button"
                    className="behavior-modal-example"
                    onClick={() => applyExample(example.text)}
                    tabIndex={examplesOpen ? 0 : -1}
                  >
                    <span className="behavior-modal-example-title">{example.title}</span>
                    <span className="behavior-modal-example-text">{example.text}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 3 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            sx={{
              color: '#5616ea',
              borderColor: '#5616ea',
              '&:hover': { borderColor: '#5e0fb3', backgroundColor: '#f3eaff' },
            }}
          >
            {t('cancel')}
          </Button>

          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={filledCount === 0}
            sx={{
              backgroundColor: '#5616ea',
              '&:hover': { backgroundColor: '#4a14d6' },
            }}
          >
            {t('add')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddBehaviorModal;
